import { Component, Input, OnInit, OnDestroy, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { GenericFormlyFormComponent } from '@intalio/formly-builder/bootstrap';
import { ServicesFormService } from '../../../services/services-form.service';

/**
 * Task Viewer - Tab 1: Initial Form
 * Displays the original submitted form data
 * Conditionally editable based on isReadonly input
 */
@Component({
  selector: 'app-task-initial-form-tab',
  standalone: true,
  templateUrl: './task-initial-form-tab.component.html',
  styleUrls: ['./task-initial-form-tab.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GenericFormlyFormComponent,
  ]
})
export class TaskInitialFormTabComponent implements OnInit, OnDestroy {
  @Input() requestTypeLogicalId: string = '';
  @Input() formData: string = '';
  @Input() taskRequest: any = null;
  @Input() pageType: 'inbox' | 'sent' | 'completed' | 'myRequests' = 'inbox';
  @Input() isReadonly: boolean = true;
  @Input() requestId: string | null = null;
  @Input() taskId: string | null = null;

  @ViewChild(GenericFormlyFormComponent) genericForm?: GenericFormlyFormComponent;

  /** Raw form definition JSON passed to the library */
  formDefinition: any = null;
  model: any = {};
  isLoading: boolean = false;
  isSaving: boolean = false;
  errorMessages: string[] = [];
  successMessage: string = '';
  schemaLoaded: boolean = false;

  /**
   * Action buttons handed to fb-bs-generic-formly-form. Empty array suppresses
   * the library's built-in Submit/Save Draft defaults entirely. When the form
   * is editable we expose a single 'update_formdata' action — same pattern the
   * frontend uses for its Initial Form tab (see task.component.ts
   * setInitialFormActions ~line 254).
   */
  get libraryActions(): string[] {
    return this.isReadonly ? [] : ['update_formdata'];
  }

  private destroy$ = new Subject<void>();
  private formSchemaFetched: boolean = false;
  private formService = inject(ServicesFormService);

  ngOnInit(): void {
    this.loadFormSchema();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadFormSchema(): void {
    if (!this.requestTypeLogicalId || this.formSchemaFetched) {
      return;
    }

    this.isLoading = true;
    this.errorMessages = [];
    this.formSchemaFetched = true;

    this.formService
      .getFormSchema(this.requestTypeLogicalId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: any) => {
          console.debug('[InitialFormTab] schema response', response);
          const schema = response.formJson;
          let rawFields: any[];

          if (Array.isArray(schema)) {
            rawFields = schema;
          } else if (schema?.components && Array.isArray(schema.components)) {
            rawFields = schema.components;
          } else {
            rawFields = [];
          }

          // Pre-initialize containers that Formly expressions try to assign into.
          // Schemas like `expressions: { "props.datepickerOptions.min": "..." }`
          // throw "Cannot set properties of undefined (setting 'min')" if
          // props.datepickerOptions doesn't exist when Formly evaluates the
          // expression. This walk seeds the target object so the assignment
          // path is valid and the form can render.
          this.ensureExpressionTargets(rawFields);
          this.formDefinition = rawFields;

          // Parse and set the model from the formData input
          if (this.formData) {
            try {
              this.model = JSON.parse(this.formData);
            } catch {
              this.model = {};
            }
          }

          this.isLoading = false;
          this.schemaLoaded = true;
        },
        error: (error: any) => {
          console.error('[TaskInitialFormTab] Error loading form schema:', error);
          this.errorMessages = ['Failed to load form schema'];
          this.isLoading = false;
        }
      });
  }

  /**
   * Walk a Formly field tree and seed empty objects at any nested target that
   * an `expressions` entry will later try to assign into. Formly assigns via
   * `field.<path> = value` and throws "Cannot set properties of undefined" if
   * an intermediate object on the path doesn't exist yet — common with schemas
   * like `expressions: { "props.datepickerOptions.min": "model.x" }` where
   * `props.datepickerOptions` itself is never declared in the JSON.
   */
  private ensureExpressionTargets(fields: any[]): void {
    if (!Array.isArray(fields)) return;
    for (const field of fields) {
      if (field?.expressions && typeof field.expressions === 'object') {
        for (const path of Object.keys(field.expressions)) {
          const segments = path.split('.');
          // We only need to ensure intermediate containers exist (not the leaf).
          // e.g. "props.datepickerOptions.min" → ensure field.props and
          // field.props.datepickerOptions are objects.
          let cursor: any = field;
          for (let i = 0; i < segments.length - 1; i++) {
            const seg = segments[i];
            if (cursor[seg] == null || typeof cursor[seg] !== 'object') {
              cursor[seg] = {};
            }
            cursor = cursor[seg];
          }
        }
      }
      if (Array.isArray(field?.fieldGroup)) {
        this.ensureExpressionTargets(field.fieldGroup);
      }
      if (field?.fieldArray && Array.isArray(field.fieldArray.fieldGroup)) {
        this.ensureExpressionTargets(field.fieldArray.fieldGroup);
      }
    }
  }

  onSave(): void {
    this.genericForm?.onSubmit('save');
  }

  onFormSubmit(event: {
    action: string;
    formData: FormData;
    requestId: number | null;
    originalModel: any;
  }): void {
    if (!this.requestId) {
      this.errorMessages = ['Request ID is missing'];
      return;
    }

    this.isSaving = true;
    this.errorMessages = [];
    this.successMessage = '';

    // Add required fields expected by the API
    event.formData.append('requestTypeLogicalId', this.requestTypeLogicalId);
    event.formData.append('requestId', this.requestId);

    this.formService
      .updateInitialForm(event.formData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isSaving = false;
          this.successMessage = 'Form saved successfully';
          setTimeout(() => { this.successMessage = ''; }, 2000);
        },
        error: (error: any) => {
          this.isSaving = false;
          this.errorMessages = [this.extractErrorMessage(error)];
          console.error('[TaskInitialFormTab] Save error:', error);
        }
      });
  }

  private extractErrorMessage(error: any): string {
    if (error.status === 400 && error.error) {
      if (Array.isArray(error.error)) return error.error.map((e: any) => e.message || e).join(', ');
      if (error.error.errors && Array.isArray(error.error.errors)) {
        return error.error.errors.map((e: any) => e.message || e).join(', ');
      }
      if (error.error.message) return error.error.message;
    }
    if (error.status === 500) return error.error?.message || 'Server error occurred';
    if (error.statusText) return `Error: ${error.statusText}`;
    return 'Failed to save form';
  }
}
